import {
	ArrowDropDown,
	ArrowRight,
	DeleteOutlineOutlined,
	DesktopMacOutlined,
	FolderOutlined,
	GetAppOutlined,
	ImageOutlined,
	InsertDriveFileOutlined,
} from "@mui/icons-material";
import { Theme, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { FileSources } from "constants/sockets";
import { getMobileSize } from "desktopWindow/desktopWindowUtils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { FileAction, FileData, useGameContext } from "../../context/GameContext";
import { useDesktopWindow } from "../../desktopWindow/context/DesktopWindowContext";
import { useWindowTilesContext } from "../../desktopWindow/context/WindowTilesContext";
import { getFileMeta, isAudio, shortenFilename } from "../helpers/fileBrowserHelpers";
import LockImage from "./padlock.png";
export const rootFolderId = isMobile ? "Home" : "Desktop";

export const useFileMap = (): Record<string, any> => {
	const { room } = useGameContext();
	if (!room) {
		return {};
	}

	const { files } = room;
	const fileMap = files.reduce((map, file) => {
		const fileId = file.id as string;
		map[fileId] = { ...file, color: "#ededed" };
		return map;
	}, {});
	const trashFolder = {
		childrenIds: [],
		color: "#ededed",
		id: "Trash",
		isDir: true,
		isRootDir: true,
		name: "Trash",
		size: 0,
	};
	// Add to files if not already there
	if (!fileMap[trashFolder.id]) {
		files.push(trashFolder);
		// Add to fileMap
		fileMap[trashFolder.id] = trashFolder;
	}
	return { files, fileMap };
};

export const useFiles = (currentFolderId: string, fileMap: Record<string, any>): FileData[] => {
	const theme = useTheme();
	return useMemo(() => {
		if (currentFolderId === "Home") {
			const folderRootDirectory = Object.entries(fileMap).reduce((acc, [key, v]) => {
				if (v.isRootDir) acc.push(key);

				return acc;
			}, [] as any);
			// const temp = {
			// 	childrenIds:
			// }
			/*eslint-disable */
			var currentFolder: any = {
				childrenIds: folderRootDirectory,
				color: "#ededed",
				id: "Home",
				isDir: true,
				isRootDir: true,
				name: "Home",
			};
		} else {
			var currentFolder = fileMap[currentFolderId];
			/*eslint-enable */
		}
		if (!currentFolder) {
			return [];
		}

		// Tries to convert file id into actual file data
		const files = currentFolder.childrenIds
			? currentFolder.childrenIds.map((fileId: string) => {
					const fileData = fileMap[fileId];
					if (!fileData) {
						return null;
					}
					// Temporarily shorten file name only when showing the file in the filebrowser.
					// When user opens the file in another window, we should show the actual name rather than the shorten one.
					return {
						...fileMap[fileId],
						name: shortenFilename(fileMap[fileId].name),
						color: theme.palette.primary.main,
						thumbnailUrl:
							(fileData.protection?.locked ? LockImage : fileData.thumbnailUrl) ||
							(fileData.isDir && fileData.protection?.locked && LockImage),
						openable: !fileData.protection?.locked,
					};
			  })
			: [];
		return files;
	}, [currentFolderId, fileMap, theme.palette.primary.main]);
};

export const useFolderChain = (currentFolderId: string, fileMap: Record<string, any>): FileData[] => {
	return useMemo(() => {
		const currentFolder = fileMap[currentFolderId];
		if (!currentFolder) {
			return [];
		}

		const folderChain = [currentFolder];

		let parentId = currentFolder.parentId;
		while (parentId) {
			const parentFile = fileMap[parentId];
			if (parentFile) {
				folderChain.unshift(parentFile);
				parentId = parentFile.parentId;
			} else {
				parentId = null;
			}
		}

		return folderChain;
	}, [currentFolderId, fileMap]);
};

type ProtectedFilePayload = {
	payload: {
		fileId: string;
	};
	fileSource: FileSources.FILE_BROWSER;
};

type FileActionHandlerProps = {
	handleSelectFolder: (folderId: string) => void;
	isFromHomeScreen?: boolean;
	setFileComponentProps?: (props: FileComponentProps) => void;
	setIsFile?: (isFile: boolean) => void;
	unlockFile?: (payload: ProtectedFilePayload) => void;
};

export const useFileActionHandler = (option: FileActionHandlerProps) => {
	const { openWindow } = useWindowTilesContext();
	const { fileMap } = useFileMap();
	return useCallback(
		async (fileData: FileData, action: FileAction) => {
			if (action === FileAction.DOUBLE_CLICK && fileData.isDir) {
				option.handleSelectFolder(fileData.id);
				return;
			}

			if (action === FileAction.DOUBLE_CLICK && !fileData.isDir) {
				const fileToOpen = fileData && fileMap[fileData?.id];
				if (!fileToOpen) {
					return;
				}

				if (fileData.protection?.locked) {
					option.unlockFile &&
						option.unlockFile({
							payload: {
								fileId: fileData.id,
							},
							fileSource: FileSources.FILE_BROWSER,
						});
				}

				if (isDesktop || option.isFromHomeScreen) {
					const isImage: boolean = fileToOpen.fileUrl.includes("jpg") || fileToOpen.fileUrl.includes("png");
					const isAudioExt: boolean = isAudio(fileToOpen.fileUrl);
					const { width: fileWidth, height: fileHeight } = await getFileMeta(fileToOpen, isImage);
					const defaultWindowWidth = isAudioExt ? 690 : 400;
					const defaultWindowHeight = isAudioExt ? 260 : 400;
					const windowWidth = Math.max(defaultWindowWidth, Math.min(fileWidth, window.innerWidth - 100));
					const windowHeight = Math.max(defaultWindowHeight, Math.min(fileHeight, window.innerHeight - 160));
					openWindow({
						id: fileToOpen.id,
						componentName: "FileViewer",
						componentProps: { file: fileToOpen },
						windowTitle: fileToOpen.name,
						size: { height: windowHeight, width: windowWidth },
						minSize: { height: defaultWindowHeight, width: defaultWindowWidth },
						resizeable: true,
					});
					return;
				} else if (isMobile) {
					const { width, height } = getMobileSize(true);
					option.setFileComponentProps!({ componentProps: { file: fileToOpen, size: { height, width } } });
					option.setIsFile!(true);
					return;
				}
			}
		},
		[fileMap, option, openWindow],
	);
};

export const useFileBrowserContainer = () => {
	const classes = useTreeItemStyles();
	const { files, fileMap } = useFileMap();
	const { componentProps } = useDesktopWindow();
	const [currentFolderId, setCurrentFolderId] = useState(componentProps?.rootFolderId || rootFolderId);
	const [pastFolder, setPastFolder] = useState<string[]>([currentFolderId]);
	const [upcomingFolder, setUpcomingFolder] = useState<string[]>([]);
	const currentFolderFiles = useFiles(currentFolderId, fileMap);
	const folderChain = useFolderChain(currentFolderId, fileMap);
	const [isFile, setIsFile] = useState<boolean>();
	const [fileComponentProps, setFileComponentProps] = useState<FileComponentProps>();
	const lastIndex = folderChain.length - 1;
	const hasParent = !!folderChain[lastIndex]?.parentId;
	const [protectedFilePayload, setProtectedFilePayload] = useState<ProtectedFilePayload>(); // source is the id of the folder/file needed for socket to know which file/folder to unlock

	const handleSelectFolder = (id: string) => {
		setCurrentFolderId(id);
		setPastFolder(pastFolder.concat(id));
	};

	const onBack = () => {
		setCurrentFolderId(pastFolder[pastFolder.length - 2]);
		setUpcomingFolder(upcomingFolder.concat(pastFolder[pastFolder.length - 1]));
		pastFolder.pop();
	};

	const onForward = () => {
		setCurrentFolderId(upcomingFolder[upcomingFolder.length - 1]);
		setPastFolder(pastFolder.concat(upcomingFolder[upcomingFolder.length - 1]));
		upcomingFolder.pop();
	};

	const onRefresh = () => {
		setCurrentFolderId(currentFolderId);
	};

	const onUpward = () => {
		setCurrentFolderId(folderChain[lastIndex]?.parentId);
		setUpcomingFolder(upcomingFolder.concat(pastFolder[pastFolder.length - 1]));
		pastFolder.pop();
	};

	/** if the file or folder is locked we want to open a modal to insert the password **/
	const unlockFile = (payload: ProtectedFilePayload) => {
		setProtectedFilePayload(payload);
	};
	const closeUnlockFileModal = () => setProtectedFilePayload(undefined);

	// on the mobile version, opening a file from the filebrowser app doesn't open a new tile but render
	// the file within the file browser app. we need to able to indicate whenever a file is open so
	// we can render that file within the same app. handleFileWithinFileBrowserApp
	const handleFileForMobile = () => {
		setIsFile(!isFile);
	};
	const handleFileAction = useFileActionHandler({
		handleSelectFolder,
		isFromHomeScreen: false,
		setFileComponentProps,
		setIsFile,
		unlockFile,
	});

	useEffect(() => {
		setIsFile(false);
	}, []);

	const renderSubTree = (id: string) => {
		const file = files.find((file) => file.id === id);
		if (file?.isDir) {
			return (
				<TreeItem
					key={file.id}
					nodeId={file.id}
					label={file.name}
					icon={getIcon(file.id, file.id === currentFolderId)}
					onClick={() => handleSelectFolder(file.id)}
					children={
						Array.isArray(file.childrenIds) ? file.childrenIds.map((node) => renderSubTree(node)) : null
					}
					classes={{
						root: classes.root,
						content: classes.content,
						label: classes.label,
						selected: classes.selected,
						group: classes.group,
						expanded: classes.expanded,
					}}
				/>
			);
		}
		return null;
	};

	return {
		files,
		currentFolderId,
		pastFolder,
		upcomingFolder,
		currentFolderFiles,
		folderChain,
		hasParent,
		isFile,
		fileComponentProps,
		protectedFilePayload,
		handleSelectFolder,
		onBack,
		onForward,
		onRefresh,
		onUpward,
		handleFileAction,
		renderSubTree,
		handleFileForMobile,
		setIsFile,
		unlockFile,
		closeUnlockFileModal,
	};
};

function getIcon(fileId: string, isSelected: boolean): JSX.Element {
	const arrowIcon: JSX.Element = isSelected ? <ArrowDropDown /> : <ArrowRight />;
	switch (fileId) {
		case "Desktop":
			return (
				<>
					<DesktopMacOutlined />
				</>
			);
		case "Documents":
			return (
				<>
					<InsertDriveFileOutlined />
				</>
			);
		case "Downloads":
			return (
				<>
					<GetAppOutlined />
				</>
			);
		case "Pictures":
			return (
				<>
					<ImageOutlined />
				</>
			);
		case "Trash":
			return (
				<>
					<DeleteOutlineOutlined />
				</>
			);
		default:
			return (
				<>
					{arrowIcon}
					<FolderOutlined />
				</>
			);
	}
}

const useTreeItemStyles = makeStyles((theme: Theme) => ({
	root: {
		fontFamily: "Nunito",
		margin: "8px 12px",
	},
	content: {
		padding: "8px 20px",
		fontWeight: 600,
		borderRadius: 8,
		"$expanded > &": {
			fontWeight: theme.typography.fontWeightRegular,
		},
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
		fontSize: 14,
	},
	group: {
		marginLeft: 0,
		"& $content": {
			paddingLeft: theme.spacing(4),
		},
	},
	expanded: {},
	selected: { "&$selected > $content": { backgroundColor: theme.palette.action.selected } },
	label: {
		fontSize: "14px !important",
		fontWeight: "inherit",
		color: "inherit",
		backgroundColor: "transparent !important",
		marginLeft: 10,
	},
}));

interface FileComponentProps {
	componentProps: { file: FileData; size: { height: number; width: number } };
}
